import { APIConfiguration } from '@savgroup-front-common/configuration';

import {
  SESSION_LOGIN_ATTEMPT_KEY,
  SESSION_LOGIN_ENDPOINT,
  SESSION_LOGOUT_ENDPOINT,
  SESSION_STATE,
  SESSION_STATE_ENDPOINT,
  UNAUTHORIZED_STATUS,
} from './App.context.constants';
import {
  SessionLoginArgs,
  SessionLogoutArgs,
  SessionSnapshot,
  SessionState,
  SessionStatePayload,
} from './App.context.types';

interface BuildSessionUrlArgs {
  endpoint: string;
  params: Record<string, string | undefined>;
}

function buildSessionUrl({ endpoint, params }: BuildSessionUrlArgs): string {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      search.set(key, value);
    }
  });

  const query = search.toString();

  return query ? `${endpoint}?${query}` : endpoint;
}

export function buildSessionLoginUrl(args?: SessionLoginArgs): string {
  const { pathname, search } = window.location;

  return buildSessionUrl({
    endpoint: SESSION_LOGIN_ENDPOINT,
    params: {
      ...args?.extraQueryParams,
      returnTo: args?.returnTo ?? `${pathname}${search}`,
    },
  });
}

export function buildSessionLogoutUrl(args?: SessionLogoutArgs): string {
  return buildSessionUrl({
    endpoint: SESSION_LOGOUT_ENDPOINT,
    params: { postLogoutRedirectUri: args?.postLogoutRedirectUri },
  });
}

export function hasAttemptedSessionLogin(): boolean {
  return sessionStorage.getItem(SESSION_LOGIN_ATTEMPT_KEY) !== null;
}

export function markSessionLoginAttempt(): void {
  sessionStorage.setItem(SESSION_LOGIN_ATTEMPT_KEY, '1');
}

export function clearSessionLoginAttempt(): void {
  sessionStorage.removeItem(SESSION_LOGIN_ATTEMPT_KEY);
}

function normalizeApiBase(url: string): string {
  return url.replace(/\/+$/, '');
}

function warnOnApiBaseMismatch(payload: SessionStatePayload): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const workerApiBase = payload.apiBaseUrl;

  if (
    !workerApiBase ||
    normalizeApiBase(workerApiBase) ===
      normalizeApiBase(APIConfiguration.apiBase)
  ) {
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(
    `[session] VITE_ENV targets ${APIConfiguration.apiBase} but the BFF worker ` +
      `targets ${workerApiBase}. Align VITE_ENV with the running worker.`,
  );
}

function resolveSessionState(response: Response): SessionState {
  if (response.ok) {
    return SESSION_STATE.ACTIVE;
  }

  if (response.status === UNAUTHORIZED_STATUS) {
    return SESSION_STATE.ABSENT;
  }

  return SESSION_STATE.UNAVAILABLE;
}

export async function fetchSessionState(): Promise<SessionSnapshot> {
  try {
    const response = await fetch(SESSION_STATE_ENDPOINT, {
      credentials: 'same-origin',
    });
    const payload: SessionStatePayload = await response
      .json()
      .catch(() => ({}));

    warnOnApiBaseMismatch(payload);

    return {
      state: resolveSessionState(response),
      clientId: payload.clientId,
      authority: payload.authority,
    };
  } catch {
    return { state: SESSION_STATE.UNAVAILABLE };
  }
}
